import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { publicTrack } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SampleShare - public page for a shared SAMPLE interview result
 * (/s/{shareId}). Shows score + role + top two strengths, and converts
 * visitors with "Try the Free Sample" / "Create Free Account" CTAs.
 */
export default function SampleShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | expired | missing
  const fired = useRef(false);

  useEffect(() => {
    axios
      .get(`${API}/api/video-interview/sample-share/${shareId}`)
      .then(({ data: d }) => {
        setData(d);
        setState("ok");
        if (!fired.current) {
          fired.current = true;
          publicTrack("sample_share_view", { share_id: shareId });
        }
      })
      .catch((e) => setState(e?.response?.status === 410 ? "expired" : "missing"));
  }, [shareId]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between max-w-4xl mx-auto w-full">
        <BrandLogo variant="lockup" iconSize={24} to="/" testid="share-brand" />
        <Link
          to="/register"
          data-testid="share-header-signup"
          className="text-xs font-semibold rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 px-4 py-2 transition-colors"
        >
          Create Free Account
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {state === "loading" && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="share-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading result…
          </div>
        )}

        {(state === "expired" || state === "missing") && (
          <div data-testid="share-expired" className="max-w-md text-center space-y-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Sample interview result</div>
            <h1 className="text-2xl font-bold tracking-tight">
              {state === "expired" ? "This share link has expired." : "This share link isn't available."}
            </h1>
            <p className="text-sm text-zinc-400 leading-relaxed">
              But you can experience the same AI interview yourself, free, no signup needed.
            </p>
            <ShareCtas />
          </div>
        )}

        {state === "ok" && data && (
          <div data-testid="share-result" className="w-full max-w-md">
            <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 sm:p-8">
              <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/[0.06] blur-3xl pointer-events-none" />
              <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.24em] font-semibold text-amber-300 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
                <Sparkles className="h-3 w-3" /> Sample AI Interview Result
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                {data.overall_score != null && (
                  <div className="relative h-32 w-32" data-testid="share-score">
                    <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(39,39,42)" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(245,158,11)" strokeWidth="8" strokeLinecap="round"
                              strokeDasharray={`${(data.overall_score / 100) * 264} 264`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-amber-300 tabular-nums">{data.overall_score}</span>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-500">Score</span>
                    </div>
                  </div>
                )}
                <div className="mt-4 text-lg font-semibold text-zinc-50" data-testid="share-role">{data.role}</div>
                {data.company && <div className="text-xs text-zinc-500 mt-0.5">{data.company}</div>}
              </div>

              {data.strengths?.length > 0 && (
                <div className="mt-6 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] p-4" data-testid="share-strengths">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-emerald-400 mb-2.5">Top strengths</div>
                  <ul className="space-y-2 text-sm text-zinc-100 leading-relaxed text-left">
                    {data.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 text-center">
                <div className="text-sm text-zinc-300 font-medium">Think you'd score higher?</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Face the same AI interviewer, free, in your browser, no signup needed.
                </div>
              </div>

              <ShareCtas className="mt-5" />
            </div>
            <div className="mt-3 text-center text-[11px] text-zinc-500">
              Sample interview on a demo role, illustrative of the Intuiterview experience.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ShareCtas({ className = "" }) {
  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 ${className}`}>
      <Link
        to="/try"
        data-testid="share-try-cta"
        onClick={() => publicTrack("guest_cta_click", { source: "sample_share" })}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-semibold px-5 py-2.5 transition-colors"
      >
        Try the Free Sample <ArrowRight className="h-4 w-4" />
      </Link>
      <Link
        to="/register"
        data-testid="share-register-cta"
        className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-700 hover:border-zinc-500 text-zinc-200 text-sm font-semibold px-5 py-2.5 transition-colors"
      >
        Create Free Account
      </Link>
    </div>
  );
}
